import React, { useState, useRef } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useTranslation } from "react-i18next";

const CategoryTree = ({
  categories,
  selectedCategory,
  onCategorySelect,
  onCategoryMove,
  onCategoryDelete,
  searchTerm,
}) => {
  const { t } = useTranslation();

  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [draggedItem, setDraggedItem] = useState(null);
  const dragOverItem = useRef(null);

  const toggleExpanded = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleDragStart = (e, category) => {
    setDraggedItem(category);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, category) => {
    e.preventDefault();
    dragOverItem.current = category;
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, targetCategory) => {
    e.preventDefault();
    if (draggedItem && targetCategory && draggedItem.id !== targetCategory.id) {
      onCategoryMove(draggedItem.id, targetCategory.id);
    }
    setDraggedItem(null);
    dragOverItem.current = null;
  };

  const filterCategories = (categories, searchTerm) => {
    if (!searchTerm) return categories;

    return categories.filter(
      (category) =>
        category.translations.en.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        category.translations.ar.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
    );
  };

  const renderCategory = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories.has(category.id);
    const isSelected = selectedCategory?.id === category.id;
    const isDraggedOver = dragOverItem.current?.id === category.id;

    return (
<div key={category.id} className="select-none">
  <div
    className={`flex items-center py-2 px-3 rounded-md cursor-pointer transition-all duration-200 hover:bg-muted group ${
      isSelected ? "bg-primary text-primary-foreground" : ""
    } ${
      isDraggedOver
        ? "bg-accent/20 border-2 border-accent border-dashed"
        : ""
    }`}
    style={{ marginLeft: `${level * 20}px` }}
    // onClick={() => onCategorySelect(category)}
    draggable
    onDragStart={(e) => handleDragStart(e, category)}
    onDragOver={(e) => handleDragOver(e, category)}
    onDrop={(e) => handleDrop(e, category)}
  >
    {/* Expand/Collapse Button */}
    <div className="w-6 flex justify-center shrink-0">
      {hasChildren ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            toggleExpanded(category.id);
          }}
          className="w-5 h-5 p-0 hover:bg-transparent"
        >
          <Icon
            name={isExpanded ? "ChevronDown" : "ChevronRight"}
            size={14}
            className={`transition-transform duration-200 ${
              isSelected
                ? "text-primary-foreground"
                : "text-muted-foreground"
            }`}
          />
        </Button>
      ) : (
        <div className="w-5 h-5 flex items-center justify-center">
          <div className={`w-1.5 h-1.5 rounded-full ${
            isSelected ? "bg-primary-foreground" : "bg-muted-foreground"
          }`}></div>
        </div>
      )}
    </div>

    {/* Category Icon */}
    <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center mr-3 shrink-0 overflow-hidden">
      <img
        src={category.image}
        alt={category.translations.en.name}
        className="w-full h-full object-cover rounded-md"
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'flex';
        }}
      />
      <div className="hidden w-full h-full items-center justify-center text-muted-foreground">
        <Icon name="Folder" size={16} />
      </div>
    </div>

    {/* Category Info */}
    <div className="flex-1 min-w-0">
      <div className="flex items-start justify-between">
        {/* Names Section */}
        <div className="flex-1 min-w-0 mr-2">
          <span
            className={`font-semibold truncate block ${
              isSelected ? "text-primary-foreground" : "text-foreground"
            }`}
          >
            {category.translations.en.name}
          </span>
          <div className="flex items-center justify-between mt-0.5">
            <span
              className={`text-sm font-normal truncate ${
                isSelected ? "text-primary-foreground/80" : "text-muted-foreground"
              }`}
            >
              {category.translations.ar.name}
            </span>
            {/* Product Count Badge - Now inline with Arabic name for better association */}
            {category.products_count > 0 && (
              <span
                className={`text-xs px-2 py-1 rounded-full font-medium ml-2 shrink-0 ${
                  isSelected
                    ? "bg-primary-foreground/20 text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {category.products_count}
              </span>
            )}
          </div>
        </div>

        {/* Actions - Appears on hover */}
        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center space-x-1 ml-2 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onCategoryDelete(category.id);
            }}
            className="w-6 h-6 p-0 hover:bg-destructive/10 hover:text-destructive-foreground transition-colors"
          >
            <Icon name="Trash2" size={12} />
          </Button>
        </div>
      </div>
    </div>
  </div>
</div>


    );
  };

  const filteredCategories = filterCategories(categories, searchTerm);

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-1 p-2">
        {filteredCategories.length > 0 ? (
          filteredCategories.map((category) => renderCategory(category))
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Search" size={48} className="mx-auto mb-4 opacity-50" />
            <p>{t("no_cat")}</p>
            {searchTerm && (
              <p className="text-sm mt-2">Try adjusting your search terms</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryTree;
